import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AppPerfCaptureConfig } from './constants';
import { fireEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { useCallback, useMemo } from 'react';
import { useMetricsContext } from '../context';
import { fg } from '@atlaskit/platform-feature-flags';

export const useAppPerfCaptureStrategy = ({
	config,
	samplingKey,
}: {
	config: AppPerfCaptureConfig;
	samplingKey: string;
}): {
	capture: (attributes: Record<string, unknown>) => void;
	enabled: boolean;
} => {
	const { page: source } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const enabled = useMemo(() => {
		if (!samplingKey) {
			return false;
		}

		if (!config.enabled || !fg('forge-ui-app-perf-tracking-enabled')) {
			return false;
		}

		// Override: capture all events.
		const captureAllFlag = fg('forge-ui-app-perf-capture-all-enabled');
		if (captureAllFlag) {
			return true;
		}

		// Capture a sample of events.
		const captureSampleFlag = fg('forge-ui-app-perf-capture-sampling-enabled');
		if (captureSampleFlag && Math.random() <= config.sampleRate) {
			return true;
		}

		return false;
	}, [config, samplingKey]);

	const capture = useCallback(
		(attributes: Record<string, unknown>): void => {
			if (!enabled) {
				return;
			}

			fireEvent(createAnalyticsEvent, {
				action: 'performance.profiled',
				actionSubject: 'forge.ui.extension',
				attributes,
				source,
			});
		},
		[source, createAnalyticsEvent, enabled],
	);

	return {
		enabled,
		capture,
	};
};
